import React, { useEffect, useState } from "react";
import { preloadJobsByLocation } from "../routes/lazyRoutes";
import { RxDoubleArrowLeft, RxEnvelopeClosed } from "react-icons/rx";
import sidebars from "../json/sidebar.json";
import { useLocation, useNavigate } from "react-router-dom";
import { useSidebar } from "../context/SidebarContext";
import { PiWarningOctagon } from "react-icons/pi";
import { FiUsers, FiBriefcase, FiFolder, FiClipboard } from "react-icons/fi";
import { useTranslation } from "react-i18next";
import { useAccountRole } from "../context/RoleContext";
import { useEmployerCompany } from "../hooks/useEmployerCompany";
import { useIsDiilAdmin } from "../hooks/useIsDiilAdmin";

// "Ажлын Групп" lives in the mobile bottom nav; mirror it here so desktop users
// can reach it too. No matching SVG exists in /img/sidebar, so it's rendered
// with the same FiUsers icon + yellow accent the mobile tab uses.
const WORKGROUP_LINK = "/job-group";
const WORKGROUP_LABEL = { mn: "Ажлын Групп", rs: "Рабочая группа", uz: "Ish guruhi" };

// The admin CV bank. Same treatment as Ажлын Групп above — no SVG asset exists for it, so
// it is drawn with a react-icon — but shown only to Diil admins, and named through i18n
// rather than a label map: it was added to all three locale files, and a second copy of the
// same three words here is one of them waiting to go stale.
const ADMIN_CV_LINK = "/admin/cv";

// The admin job board, shown to the same people and on the same terms as the CV сан row
// above. Its own path rather than one nested under /admin/cv: `active` below is a
// `startsWith`, so a child route would leave both rows lit at once.
const ADMIN_JOBS_LINK = "/admin/jobs";

// There is deliberately no "Миний зар" entry here. An employer reaches their ads
// through Профайл -> Манай компани, where they sit under the company they belong to
// alongside everything else a visitor sees — one place, not two lists of the same ads.

const Sidebar = () => {
  const [selectedMenu, setSelectedMenu] = useState(0);
  const { sidebar, setSidebar } = useSidebar();
  const navigate = useNavigate();
  const location = useLocation();
  const { t, i18n } = useTranslation();
  const { accountRole } = useAccountRole();
  const { landingCompanyId, admin: isDiilAdmin } = useEmployerCompany();
  // Whether this person is staff, asked in the one way that answers for both roles.
  // `isDiilAdmin` above comes from useEmployerCompany, which is gated on the employer role
  // — right for everything it drives here (which company row to open, what to call it), all
  // of which is employer chrome anyway. The CV сан row is not: an admin belongs to no
  // company and can be reading the site as a jobseeker, and through that hook they would
  // read false and lose the link to their own tool. Same query key, so this shares the one
  // /diil/employer/me response rather than adding a request.
  // In admin mode only — an admin browsing as a jobseeker does not get the staff rows.
  const { inAdminMode: canOpenCvBank } = useIsDiilAdmin();
  const isEmployer = accountRole === "employer";

  useEffect(() => {
    const current = sidebars.find((item) => location.pathname === item.link);
    // Clear the selection when the route isn't a listed menu item (e.g.
    // /job-group / Ажлын Групп) — otherwise the previously clicked item keeps
    // its yellow "selected" icon while another item is active.
    setSelectedMenu(current ? current.id : -1);

    if (window.innerWidth < 1024) {
      setSidebar(false);
    }
  }, [location.pathname, setSidebar]);

  return (
    <div
      // The width transition is lg-only on purpose: below lg this same panel lives in
      // Layout's mobile drawer, where it goes w-[5rem] -> w-full as the drawer slides in,
      // and animating that would widen a white panel behind the drawer for 300ms. On
      // desktop it has to glide at the same 300ms as Layout's aside and the content column,
      // or the collapse leaves a gutter between them mid-animation.
      className={`px-[16px] pt-[20px] pb-[5.5rem] lg:pb-[20px] border-r border-[#CAD5E2] h-screen flex flex-col justify-between  bg-[#fff] lg:transition-all lg:duration-300
    ${sidebar ? "sm:w-[14.75rem] w-full" : "w-[5rem] sm:w-[5rem]"}
  `}
    >
      <div className="flex flex-col gap-[32px]">
        <div className="flex items-center justify-between">
          {sidebar ? (
            <img
              onClick={() => {
                navigate("/");
              }}
              src="/img/logo/logo.svg"
              alt="logo"
              className={`diil-logo-dark transition-opacity duration-100 cursor-pointer `}
            />
          ) : (
            <img
              onClick={() => {
                navigate("/");
              }}
              src="/img/logo/logoc.svg"
              alt="logo"
              className={`diil-logo-dark transition-opacity duration-100 cursor-pointer`}
            />
          )}
          <button
            onClick={() => setSidebar(!sidebar)}
            className={`w-[40px] h-[40px] p-2 rounded-full border-[3px] border-transparent hover:border-[#E2E8F0] flex items-center justify-center transition-all duration-300 ${
              !sidebar && "hidden"
            }`}
          >
            {/* No `md:rotate-180` any more: md and lg both switch at 1024, so the lg:rotate-0
                that followed it always won and the arrow never actually flipped. */}
            <RxDoubleArrowLeft className="text-[24px]" />
          </button>
        </div>

        <div>
          <button
            onClick={() => {
              if (isEmployer) {
                // Same destination "Профайл → Манай компани → Засах" uses: the aggregate
                // for an admin, who posts for many businesses and has no company page of
                // their own, and that one company's page for everyone else. No company yet
                // (registered but never posted) — send them to Профайл instead, where the
                // registration/notice flow for that case already lives; there's no room to
                // show it here.
                navigate(
                  isDiilAdmin ? "/employer/companies"
                    : landingCompanyId ? `/company/${landingCompanyId}`
                    : "/user/profile"
                );
                return;
              }
              navigate("/chat");
            }}
            className={`w-full flex items-center text-[#fff] hover:text-[#0F172B] hover:bg-[#29EAFF] bg-[#020618] rounded-[99px] p-1.5 transition-all duration-300 group ${
              sidebar ? "gap-2" : "justify-center"
            }`}
          >
            <div className="flex items-center justify-center bg-[#1D293D] group-hover:bg-[#0F172B] rounded-full p-2 transition-all duration-300 text-center">
              {isEmployer ? (
                <FiBriefcase className="w-5 h-5" />
              ) : (
                <>
                  <img
                    src="/icon/voice.svg"
                    alt="icon"
                    className="group-hover:hidden"
                  />
                  <img
                    src="/icon/voiceh.svg"
                    alt="icon"
                    className="hidden group-hover:block"
                  />
                </>
              )}
            </div>
            {sidebar && (
              <p className={`text-center md:w-auto w-full md:me-0 me-14 ${isEmployer ? (isDiilAdmin ? "text-[17px] font-medium whitespace-nowrap" : "text-[15px] whitespace-nowrap") : ""}`}>
                {isEmployer
                  // An admin has no company of their own — the ads are what this leads to,
                  // so name it after them rather than after a company they don't have.
                  ? (isDiilAdmin ? t('sidebar.myPosts', 'Миний зарууд') : t('sidebar.myCompany', 'Манай байгууллага'))
                  : t('sidebar.giveInterview')}
              </p>
            )}
          </button>
        </div>

        <div className="flex flex-col">
          {(() => {
            const lang = i18n.language === 'ru' ? 'rs' : i18n.language === 'uz' ? 'uz' : 'mn';
            // Ажлын Групп sits 2nd — right under Ажлын байр (sidebars[0]).
            const items = [
              sidebars[0],
              { custom: true, link: WORKGROUP_LINK, label: WORKGROUP_LABEL[lang], icon: FiUsers, bounce: true },
              ...sidebars.slice(1),
              // CV сан — Diil admins only, and last, because it is a staff tool rather than
              // part of the product every visitor sees. The page and the server both refuse
              // non-admins; hiding the row just spares everyone else a link they can't open.
              ...(canOpenCvBank
                ? [
                  { custom: true, link: ADMIN_CV_LINK, label: t("sidebar.cvBank", "CV сан"), icon: FiFolder },
                  // Directly under CV сан: the two are the staff pair, one rooted on people
                  // and one on ads, and an admin moves between them.
                  { custom: true, link: ADMIN_JOBS_LINK, label: t("sidebar.adminJobs", "Админ анкет"), icon: FiClipboard },
                ]
                : []),
            ];
            const rowClass = `w-full p-1 flex items-center hover:bg-[#E2E8F0] transition-all duration-300 rounded-[999px] cursor-pointer ${sidebar ? "gap-2" : "justify-center"}`;

            return items.map((item) => {
              // Ажлын Групп — react-icon + yellow accent (no matching SVG asset).
              if (item.custom) {
                const active = location.pathname.startsWith(item.link);
                const Icon = item.icon;
                return (
                  <div key={item.link} onClick={() => navigate(item.link)} className={rowClass}>
                    <span
                      className="relative flex items-center justify-center w-10 h-10 rounded-full flex-none transition-colors"
                      style={{ background: active ? "#FFE83E" : "#ffffff" }}
                    >
                      {/* Inline colour, not a text-[#020618] class — dark mode
                          remaps that class to light and the icon vanishes on the
                          white circle. */}
                      <Icon className="text-[20px]" style={{ color: "#020618" }} />
                      {/* Phone drawer only — the desktop sidebar goes without it. */}
                      {item.bounce && !active && <span className="lg:hidden absolute -top-1.5 -right-1.5 text-[13px] leading-none animate-bounce select-none">🤩</span>}
                    </span>
                    {sidebar && (
                      <p className={`text-[#020618] whitespace-nowrap ${active && "font-bold"}`}>{item.label}</p>
                    )}
                  </div>
                );
              }
              // Standard menu item — active state drives the icon swap + bold label.
              const active = selectedMenu === item.id;
              return (
                <div
                  key={item.id}
                  onClick={() => { setSelectedMenu(item.id); navigate(item.link); }}
                  // The map route drags leaflet in with it — start that chunk on hover so
                  // the click doesn't wait on a download.
                  onMouseEnter={item.link === "/location" ? preloadJobsByLocation : undefined}
                  className={rowClass}
                >
                  <img src={active ? item.selectedImg : item.img} alt="icon" />
                  {sidebar && (
                    <p className={`text-[#020618] whitespace-nowrap ${active && "font-bold"}`}>
                      {item.name[lang]}
                    </p>
                  )}
                </div>
              );
            });
          })()}
        </div>
      </div>
      {sidebar ? (
        <div>
          {/* Contact */}
          <div
            onClick={() => navigate("/contact")}
            className={`w-full p-1 flex items-center hover:bg-[#E2E8F0] transition-all duration-300 rounded-[999px] cursor-pointer ${
              sidebar ? "gap-2" : "justify-center"
            }`}
          >
            <div className="flex items-center justify-center w-9 h-9 rounded-full border-2 border-transparent bg-[#fff]">
              <RxEnvelopeClosed size={20} />
            </div>
            <p className="text-[#020618] whitespace-nowrap">{t('sidebar.contact')}</p>
          </div>

          {/* Feedback */}
          <div
            onClick={() => navigate("/feedback")}
            className={`w-full p-1 flex items-center hover:bg-[#E2E8F0] transition-all duration-300 rounded-[999px] cursor-pointer ${
              sidebar ? "gap-2" : "justify-center"
            }`}
          >
            <div className="flex items-center justify-center w-9 h-9 rounded-full border-2 border-transparent bg-[#fff]">
              <PiWarningOctagon size={20} />
            </div>
            <p className="text-[#020618] whitespace-nowrap">{t('sidebar.feedback')}</p>
          </div>
        </div>
      ) : (
        <div>
          {/* Contact (icon only) */}
          <div
            onClick={() => {
              navigate("/contact");
            }}
            className={` p-1 flex items-center hover:bg-[#E2E8F0] transition-all duration-300 rounded-[999px] cursor-pointer w-[47px] h-[47px] ${
              sidebar ? "gap-2" : "justify-center"
            }`}
          >
            <div className="flex items-center justify-center w-10 h-10 rounded-full border-2 border-transparent bg-[#fff]">
              <RxEnvelopeClosed size={20} />
            </div>
          </div>

          {/* Feedback (icon only) */}
          <div
            onClick={() => {
              navigate("/feedback");
            }}
            className={` p-1 flex items-center hover:bg-[#E2E8F0] transition-all duration-300 rounded-[999px] cursor-pointer w-[47px] h-[47px] ${
              sidebar ? "gap-2" : "justify-center"
            }`}
          >
            <div className="flex items-center justify-center w-10 h-10 rounded-full border-2 border-transparent bg-[#fff]">
              <PiWarningOctagon size={20} />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Sidebar;
